import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getValueFromResource, normalizeDateFormat } from '../../common/timeLiveFunction'
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import PageSizeSelect from '../../common/PageSizeSelect';
import ScheduleVSTrackingHoursList from './ScheduleVSTrackingHoursList';
import { startOfMonth, endOfMonth, addMonths, subMonths, isAfter, isBefore, differenceInMonths } from 'date-fns';
import { useReactTable, getCoreRowModel, getExpandedRowModel, getSortedRowModel } from '@tanstack/react-table';
import moment from 'moment';
import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
import { ShowToast } from '../../common/Toastr';
import CustomPagination from "../../common/Pagination";
import { DateRangePicker } from 'rsuite';
import ScheduleVSTrackingFilter from './ScheduleVSTrackingFilter';
import ScheduleVSTrackingGroupBy from './ScheduleVSTrackingGroupBy';
import { Dropdown } from 'react-bootstrap';
import * as XLSX from 'xlsx-js-style';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import { setPageSize, updatePagination } from './SchedulerSlice';
import DatePickerRanges from '../../common/DatePickerRanges';

const ScheduleVSTackingHours = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { CurrentCultureDateFormat } = useAppSelector((state) => state.globalState);
    const { pageNo, pageSize, search, startRec } = useAppSelector((state) => state.scheduler);
    const [sorting, setSorting] = useState<any[]>([]);
    const [columnOrder, setColumnOrder] = useState<any[]>([]);
    const [columnSizing, setColumnSizing] = useState<any>({});
    const [showFilters, setShowFilters] = useState(false);
    const today = new Date();
    const [SelectedStartDate, setSelectedStartDate] = useState<Date>(startOfMonth(today));
    const [SelectedEndDate, setSelectedEndDate] = useState<Date>(endOfMonth(today));
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    // const [groupByLevels, setGroupByLevels] = useState<(string | null)[]>(['project', 'None', 'None']);
    const [groupByLevels, setGroupByLevels] = useState<(string | null)[]>(['Project', null, null]);
    const [activeFilters, setActiveFilters] = useState<{ client?: string; project?: string; task?: string }>({});
    document.title = `Scheduled vs Tracked Hours | Livetecs`;

    // useEffect(() => {
    //     const previousOverflow = document.body.style.overflow;
    //     document.body.style.overflow = 'hidden';
    //     return () => {
    //         document.body.style.overflow = previousOverflow;
    //     };
    // }, []);

    const resetToFirstPage = useCallback((size: number) => {
        dispatch(updatePagination({
            startRec: 0,
            pageSize: size,
            pageNo: 1,
            search: search
        }));
    }, [dispatch, search]);

    const totalItems = useMemo(() => data?.[0]?.TotalRecords ?? 0, [data]);
    const handlePageSizeChange = useCallback((event: any) => {
        const { value } = event;
        dispatch(setPageSize(Number(value)));
        resetToFirstPage(Number(value));
    }, [dispatch, resetToFirstPage]);

    const handlePageChange = useCallback((pageNumber: any) => {
        dispatch(updatePagination({
            startRec: (pageNumber - 1) * Number(pageSize),
            pageSize: pageSize,
            pageNo: pageNumber,
            search: search
        }));
    }, [dispatch, pageSize, search]);

    const handleApplyFilter = (newFilters: any) => {
        const { client, project, task } = newFilters;
        setActiveFilters({ client, project, task });
        fetchData(SelectedStartDate, SelectedEndDate, client, project, task);
    };

    const handleClearFilter = () => {
        setActiveFilters({});
        fetchData(SelectedStartDate, SelectedEndDate);
    };

    const shiftDateRange = (direction: "previous" | "next") => {
        let newStart, newEnd;
        if (direction === "previous") {
            newStart = startOfMonth(subMonths(SelectedStartDate, 1));
            newEnd = endOfMonth(newStart);
        } else {
            newStart = startOfMonth(addMonths(SelectedStartDate, 1));
            newEnd = endOfMonth(newStart);
        }
        setSelectedStartDate(newStart);
        setSelectedEndDate(newEnd);
        fetchData(newStart, newEnd, activeFilters.client, activeFilters.project, activeFilters.task);
    };

    const fetchData = async (start: Date, end: Date, clientIds?: any, projectIds?: any, taskIds?: any, groupBy?: (string | null)[]) => {
        setLoading(true);
        const StartDate = moment(start).format('YYYY-MM-DD');
        const EndDate = moment(end).format('YYYY-MM-DD');

        const levels = (groupBy ?? groupByLevels).filter(Boolean) as string[];
        const groupByQuery = levels.map((v, i) => `groupBy${i + 1}=${encodeURIComponent(v)}`).join('&');

        const response = await fetchTimeliveAPI(`api/Scheduler/GetAccountScheduledVSTrackingHours/${StartDate}/${EndDate}/${pageNo}/${pageSize}?clientIds=${clientIds ?? ''}&projectIds=${projectIds ?? ''}&taskIds=${taskIds ?? ''}${groupByQuery ? `&${groupByQuery}&${search}` : ''}`, 'GET', '', true);
        const json = await response.json();
        if (json?.StatusCode === '200') {
            setData(json?.Data || []);
            setLoading(false);
        } else {
            setData(json?.Data || []);
            setLoading(false);
        }
    };

    const handleGroupByChange = (levels: (string | null)[]) => {
        setGroupByLevels(levels);
        fetchData(SelectedStartDate, SelectedEndDate, activeFilters.client, activeFilters.project, activeFilters.task, levels);
    };

    // useEffect(() => {
    //     fetchData(SelectedStartDate, SelectedEndDate);
    // }, []);
    useEffect(() => {
        fetchData(SelectedStartDate, SelectedEndDate, activeFilters.client, activeFilters.project, activeFilters.task);
    }, [pageNo, pageSize]);

    const handleDateRangeChange = (value: [Date | null, Date | null] | null) => {
        if (value && value[0] && value[1]) {
            const start = value[0];
            const end = value[1];
            const monthsDiff = differenceInMonths(end, start);
            if (monthsDiff >= 3) {
                ShowToast('Error', 'Please select a date range of maximum 3 months', "error", "toast-top-right");
                return;
            }

            setSelectedStartDate(start);
            setSelectedEndDate(end);
            fetchData(start, end, activeFilters.client, activeFilters.project, activeFilters.task);
        }
    };

    const formatHoursToHHMMSS = (decimalHours: number) => {
        const totalSeconds = Math.round((decimalHours || 0) * 3600);
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(hrs)}:${pad(mins)}:${pad(secs)}h`;
    };

    const { scheduledTotal, trackedTotal } = useMemo(() => {
        const scheduledSum = data.reduce((sum, item) => sum + (Number(item?.ScheduledHours) || 0), 0);
        const trackedSum = data.reduce((sum, item) => sum + (Number(item?.TrackingHours) || 0), 0);
        return {
            scheduledTotal: formatHoursToHHMMSS(scheduledSum),
            trackedTotal: formatHoursToHHMMSS(trackedSum),
        };
    }, [data]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .custom-date-range-picker .rs-picker-toggle-value {
              white-space: nowrap !important;
              overflow: visible !important;
              text-overflow: unset !important;
              color: #172b4d !important;
             font-weight: 600 !important;
            }
            .custom-date-range-picker .rs-picker-toggle {
              width: auto !important;
              min-width: fit-content !important;
              padding-right: 8px !important;
              padding-left: 8px !important;
              padding-top: 6px !important;
              padding-bottom: 6px !important;
            }
            .custom-date-range-picker .rs-picker-toggle-caret {
              display: none !important;
            }
            .custom-date-range-picker .rs-picker-toggle,
            .custom-date-range-picker .rs-picker-toggle:focus,
            .custom-date-range-picker .rs-picker-toggle:active,
            .custom-date-range-picker .rs-picker-toggle:focus-visible {
              border-color: transparent !important;
              box-shadow: none !important;
              outline: none !important;
              background-color: transparent !important;
              color: #172b4d !important;
                 font-weight: 600 !important;
            }
             .custom-date-range-picker .rs-picker-toggle-caret {
              display: none !important;
            }
            .custom-date-range-picker .rs-picker-toggle-clean {
              display: none !important;
            }
            .custom-date-range-picker {
            display: flex;
            align-items: center;
            }
          `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const table = useReactTable({
        data,
        columns: [],
        state: { sorting, columnOrder, columnSizing },
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getSubRows: (row: any) => row.SubRowsList,
    });

    const getUtilizationLabel = (value: number) => {
        if (value < 90) return 'Underutilized';
        if (value > 110) return 'Overutilized';
        return 'Optimal';
    };

    const flattenAllRows = (rows: any[], level = 0): any[] => {
        let result: any[] = [];
        rows.forEach((row: any) => {
            result.push({ ...row, __level: level });
            if (row.SubRowsList && row.SubRowsList.length > 0) {
                result = result.concat(flattenAllRows(row.SubRowsList, level + 1));
            }
        });
        return result;
    };

    const buildExportRows = () => {
        const allRows = flattenAllRows(data);
        return allRows.map((row: any) => {
            const scheduledHours = Number(row.ScheduledHours);
            const differenceHours = Number(row.DifferenceHours);
            const statusVal = row.StatusPercentage != null ? Number(row.StatusPercentage) : null;
            const hasStatusVal = statusVal != null && !isNaN(statusVal);
            const utilizationLabel = scheduledHours === 0 ? '-' : (hasStatusVal ? getUtilizationLabel(statusVal) : '-');
            const statusPercentLabel = differenceHours < 0 ? '-' : (hasStatusVal ? `${statusVal}%` : '-');

            return {
                level: row.__level,
                Name: row.GroupLabel || '',
                ScheduledHours: formatHoursToHHMMSS(Number(row.ScheduledHours) || 0),//ow.ScheduledHours || 0,
                TrackingHours: formatHoursToHHMMSS(Number(row.TrackingHours) || 0),//row.TrackingHours || 0,
                DifferenceHours: formatHoursToHHMMSS(Number(row.DifferenceHours) || 0),//row.DifferenceHours || 0,
                Utilization: utilizationLabel,
                StatusPercent: statusPercentLabel
            };
        });
    };

    const handleExport = (type: 'xlsx' | 'csv' | 'pdf') => {
        const rows = buildExportRows();

        if (type === 'pdf') {
            const doc = new jsPDF();

            doc.setFontSize(14);
            doc.text('Schedule Vs Tracking Hours', 14, 15);
            doc.setFontSize(10);

            autoTable(doc, {
                startY: 26,
                head: [['Name', 'Scheduled', 'Tracked', 'Difference', 'Utilization', 'Progress %']],
                body: rows.map(r => [
                    `${'    '.repeat(r.level)}${r.Name}`,
                    r.ScheduledHours,
                    r.TrackingHours,
                    r.DifferenceHours,
                    r.Utilization,
                    r.StatusPercent
                ]),
                headStyles: {
                    fillColor: [217, 217, 217],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    halign: 'center'
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3
                },
                columnStyles: {
                    0: { cellWidth: 60 }
                }
            });

            doc.save(`Livetecs_${moment().format('YYYYMMDD')}.pdf`);
            return;
        }

        const exportData = rows.map(r => ({
            'Name': `${'    '.repeat(r.level)}${r.Name}`,
            'Scheduled Hours': r.ScheduledHours,
            'Tracked Hours': r.TrackingHours,
            'Difference': r.DifferenceHours,
            'Utilization': r.Utilization,
            'Progress %': r.StatusPercent
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);

        const range = XLSX.utils.decode_range(worksheet['!ref'] || "A1:F1");
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + "1";
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                font: { bold: true, color: { rgb: "000000" } },
                fill: { fgColor: { rgb: "D9D9D9" } },
                alignment: { horizontal: "center" },
                border: {
                    bottom: { style: "thin", color: { rgb: "BFBFBF" } },
                    right: { style: "thin", color: { rgb: "BFBFBF" } }
                }
            };
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ScheduleData");

        if (type === 'xlsx') {
            XLSX.writeFile(workbook, `Livetecs_${moment().format('YYYYMMDD')}.xlsx`);
        } else {
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `Livetecs_${moment().format('YYYYMMDD')}.csv`);
            link.click();
        }
    };

    return (
        <div>
            <div className="card card-custom admin-card admin-card-Scroll">
                <div className="card-header card-drop sticky-top bg-white fixed-top admin-card-header-sticky"
                    // style={{ minHeight: 60, zIndex: 1050 }}
                >
                    <h3 className="card-title fw-bold headerTitle" style={{ lineHeight: 1 }}>
                        {getValueFromResource('Scheduled vs Tracked Hours', dispatch, t)}
                    </h3>
                    <div className="d-flex gap-2">
                        <div className="d-flex align-items-center flex-shrink-0" style={{ position: 'relative', backgroundColor: 'white', border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '4px', height: '36px' }}>
                            <a onClick={() => shiftDateRange("previous")} className="btn btn-sm btn-icon btn-icon-primary"><i className="ki-outline ki-left fs-3"></i></a>
                            <DateRangePicker
                                value={[SelectedStartDate, SelectedEndDate]}
                                onChange={handleDateRangeChange}
                                // ranges={DatePickerRanges()}
                                ranges={[]}
                                style={{ width: 'auto' }}
                                className="custom-date-range-picker"
                                appearance="subtle"
                                placement="bottomEnd"
                                format={normalizeDateFormat(CurrentCultureDateFormat.toString())}
                                editable={false}
                                cleanable={false}
                            />
                            <a onClick={() => shiftDateRange("next")} className="btn btn-sm btn-icon btn-icon-primary"><i className="ki-outline ki-right fs-3"></i></a>
                        </div>
                        <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                        <div className="d-flex align-items-center">
                            <PageSizeSelect onChange={handlePageSizeChange} />
                        </div>
                        <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                        <div>
                            <CustomPagination
                                currentPage={pageNo}
                                onPageChange={handlePageChange}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                start={startRec}
                                t={t}
                            >
                            </CustomPagination>
                        </div>
                        <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                        <div className="btn-group" style={{ display: 'block' }}>
                            <button
                                type="button"
                                className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                                onClick={() => handleExport('pdf')}
                            >
                                {getValueFromResource('Export', dispatch, t)}

                            </button>
                            <button type="button" className="btn btn-primary btn-bs-btn-font-weight btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                <i className="fa fa-angle-down"></i>
                            </button>
                            <ul className=" dropdown-menu menu-rounded px-1 py-1 fs-2" role="menu">
                                <div className='menu-hover-bg-sec'>
                                    <Dropdown.Item
                                        className="menu-item me-lg-0"
                                        onClick={() => handleExport('pdf')}
                                    >
                                        <span className="menu-icon custom-menu mb-2">
                                            <i className="bi bi-file-earmark-pdf fs-6 iconColor">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                        </span>
                                        <span className="menu-title custom-menu-fontsize" style={{ paddingRight: 6 }}>
                                            {getValueFromResource('Export PDF', dispatch, t)}
                                        </span>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        className="menu-item me-lg-0"
                                        onClick={() => handleExport('xlsx')}
                                    >
                                        <span className="menu-icon custom-menu mb-2">
                                            <i className="bi bi-file-earmark-excel fs-6 iconColor">
                                                <span className="path1"></span>
                                                <span className="path2"></span>
                                            </i>
                                        </span>
                                        <span className="menu-title custom-menu-fontsize" style={{ paddingRight: 6 }}>
                                            {getValueFromResource('Export XLSX', dispatch, t)}
                                        </span>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        className="menu-item me-lg-0"
                                        onClick={() => handleExport('csv')}
                                    >
                                        <span className="menu-icon custom-menu mb-2">
                                            <i className="bi bi-filetype-csv fs-6 iconColor"></i>
                                        </span>
                                        <span className="menu-title custom-menu-fontsize" style={{ paddingRight: 6 }}>
                                            {getValueFromResource('Export CSV', dispatch, t)}
                                        </span>
                                    </Dropdown.Item>
                                </div>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="card-body p-0 admin-card-body">
                    <div className="py-2" style={{ flexShrink: 0 }}>
                        <ScheduleVSTrackingFilter onApplyFilter={handleApplyFilter} onClearFilter={handleClearFilter} />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                        <ScheduleVSTrackingGroupBy groupByLevels={groupByLevels} onChange={handleGroupByChange} scheduledTotal={scheduledTotal} trackedTotal={trackedTotal} />
                    </div>
                    <div style={{ flex: 1, overflowY: 'hidden', minHeight: 0 }}>
                        <ScheduleVSTrackingHoursList data={data} loading={loading} table={table} />
                    </div>
                </div>
                {/* <div className="py-2" style={{ overflow: 'hidden' }}>
                    <ScheduleVSTrackingFilter onApplyFilter={handleApplyFilter} onClearFilter={handleClearFilter} />
                </div>
                <ScheduleVSTrackingGroupBy groupByLevels={groupByLevels} onChange={handleGroupByChange} scheduledTotal={scheduledTotal} trackedTotal={trackedTotal} />
                <div className="card-body p-0">
                    <ScheduleVSTrackingHoursList data={data} loading={loading} table={table} />
                </div> */}
            </div>
        </div>
    )
}

export default ScheduleVSTackingHours